import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as STooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { LogOut, Plus, X, FileText, Send, CreditCard, Check, AlertTriangle, Loader2, Download, Clock, Euro, Palette, ShoppingBag, ChevronRight, Paperclip, Trash2, ExternalLink, User, Edit2, Globe, Sparkles, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";

function Tip({ label, children }) {
  return (
    <STooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="bg-[#2F4F4F] text-white text-[11px] px-2.5 py-1 rounded-md">{label}</TooltipContent>
    </STooltip>
  );
}

export default function ClientDashboard() {
  const { user, logout, checkAuth } = useAuth();
  const { dark, toggle: toggleTheme } = useTheme();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribeDialog, setSubscribeDialog] = useState(null);
  const [cancelDialog, setCancelDialog] = useState(null);
  const [modDialog, setModDialog] = useState(false);
  const [modType, setModType] = useState("site_update");
  const [modMessage, setModMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [brandingLoading, setBrandingLoading] = useState(null);
  const [detailDialog, setDetailDialog] = useState(null);
  const [modFiles, setModFiles] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [planChangeOpen, setPlanChangeOpen] = useState(false);
  const [planChangePlan, setPlanChangePlan] = useState("");
  const [planChangeOptions, setPlanChangeOptions] = useState([]);
  const [planChangeMessage, setPlanChangeMessage] = useState("");
  const [plansCatalog, setPlansCatalog] = useState(null);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const paymentRef = { current: null };

  const loadSubscription = useCallback(async () => {
    try { const { data } = await api.get("/client/subscription"); setSubscription(data); }
    catch { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  }, []);

  const loadPayments = useCallback(async () => {
    try { const { data } = await api.get("/payments/history"); setPayments(data); }
    catch (err) { console.warn("Could not load payments:", err?.message); }
  }, []);

  const loadPendingPlanChange = useCallback(async () => {
    try { const { data } = await api.get("/client/plan-change-request"); setPendingPlanChange(data?.pending_request || null); }
    catch (err) { console.warn("Could not load pending plan change:", err?.message); }
  }, []);

  const loadPlansCatalog = useCallback(async () => {
    try { const { data } = await api.get("/plans"); setPlansCatalog(data); }
    catch (err) { console.warn("Could not load plans catalog:", err?.message); }
  }, []);

  useEffect(() => { loadSubscription(); loadPayments(); loadPendingPlanChange(); loadPlansCatalog(); }, [loadSubscription, loadPayments, loadPendingPlanChange, loadPlansCatalog]);

  // Check for payment return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const paymentStatus = params.get("payment");
    if (sessionId && paymentStatus === "success") {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paymentStatus === "cancelled") {
      toast.info("Paiement annulé");
      window.history.replaceState({}, "", window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Welcome popup after contract signature
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("welcome") === "true") {
      setTimeout(() => setWelcomeOpen(true), 600);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleWelcomeActivate = () => {
    setWelcomeOpen(false);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="payment-section"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.info("Vérification du paiement en cours. Vous serez notifié.");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        toast.success("Paiement confirmé ! Merci.");
        loadSubscription();
        loadPayments();
        checkAuth();
        return;
      }
      if (data.status === "expired") {
        toast.error("Session de paiement expirée.");
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      toast.error("Erreur lors de la vérification du paiement.");
    }
  };

  const handlePaySubscription = async () => {
    setPaymentLoading(true);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/payments/create-checkout", { origin_url: originUrl });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la création du paiement");
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleBillingPortal = async () => {
    setBillingLoading(true);
    try {
      const { data } = await api.post("/payments/billing-portal", { return_url: window.location.origin + "/client" });
      if (data.url) window.open(data.url, "_blank");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur d'accès au portail de facturation");
    } finally {
      setBillingLoading(false);
    }
  };

  const handleOrderBranding = async (brandingKey) => {
    setBrandingLoading(brandingKey);
    try {
      const originUrl = window.location.origin;
      const { data } = await api.post("/payments/create-checkout", { origin_url: originUrl, branding_key: brandingKey });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de la commande");
    } finally {
      setBrandingLoading(null);
    }
  };

  const handleSubscribeOption = async () => {
    if (!subscribeDialog) return;
    setActionLoading(true);
    try { const { data } = await api.post(`/client/options/${subscribeDialog.key}/subscribe`); toast.success(data.message); await loadSubscription(); setSubscribeDialog(null); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const openPlanChangeDialog = () => {
    setPlanChangePlan(subscription?.plan?.key || "");
    setPlanChangeOptions(subscription?.active_options?.map(o => o.key) || []);
    setPlanChangeMessage("");
    setPlanChangeOpen(true);
  };

  const togglePlanChangeOption = (optKey) => {
    setPlanChangeOptions(prev => prev.includes(optKey) ? prev.filter(o => o !== optKey) : [...prev, optKey]);
  };

  const handleSubmitPlanChange = async () => {
    if (!planChangePlan) { toast.error("Sélectionnez un forfait."); return; }
    setActionLoading(true);
    try {
      const { data } = await api.post("/client/plan-change-request", {
        requested_plan: planChangePlan,
        requested_options: planChangeOptions,
        message: planChangeMessage,
      });
      toast.success(data.message);
      setPlanChangeOpen(false);
      loadPendingPlanChange();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur lors de l'envoi de la demande.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOption = async () => {
    if (!cancelDialog) return;
    setActionLoading(true);
    try { const { data } = await api.post(`/client/options/${cancelDialog.key}/cancel`); toast.success(data.message); await loadSubscription(); setCancelDialog(null); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setActionLoading(false); }
  };

  const handleModification = async () => {
    if (!modMessage.trim()) return;
    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("type", modType);
      formData.append("message", modMessage);
      modFiles.forEach(f => formData.append("files", f));
      await api.post("/client/request-modification", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Votre demande a été envoyée");
      setModDialog(false); setModMessage(""); setModFiles([]);
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Erreur"); }
    finally { setActionLoading(false); }
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files || []);
    const maxSize = 5 * 1024 * 1024;
    const valid = selected.filter(f => {
      if (f.size > maxSize) { toast.error(`${f.name} dépasse 5 Mo`); return false; }
      return true;
    });
    setModFiles(prev => [...prev, ...valid]);
    e.target.value = "";
  };

  const removeFile = (index) => setModFiles(prev => prev.filter((_, i) => i !== index));

  const openContractPdf = async () => {
    try {
      const response = await api.get("/client/contract-pdf", { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "contrat-monbeausite.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Contrat téléchargé avec succès");
    } catch { toast.error("Erreur lors du téléchargement du contrat"); }
  };

  if (loading) return <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1F3535]" /></div>;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="mbs-dash min-h-screen bg-[#FBF9F5]">
      <header className="border-b border-[#BFBFBF]/60 bg-white/95 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2.5">
              <img src="/api/static/images/web/logo_mbs.png" alt="" className="h-9 w-9 object-contain rounded-xl ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" />
              <span className="text-[15px] font-semibold text-[#1F3535] tracking-tight font-body">
                MonBeauSite<span className="text-[#C46A4A]">.</span>com
              </span>
            </div>
            <Badge variant="outline" className="text-[#1F3535] border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[10px] tracking-[0.16em] uppercase font-semibold nav-text px-2.5">Client</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#6B6B6B] hidden sm:inline font-body">{user?.denomination_sociale || user?.email}</span>
            <Tip label={dark ? "Mode clair" : "Mode sombre"}>
              <button data-testid="theme-toggle-client" onClick={toggleTheme} className="text-[#6B6B6B] hover:text-[#C46A4A] transition-colors p-1.5 rounded-full hover:bg-[#C46A4A]/5">
                {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </Tip>
            <Tip label="Se déconnecter"><Button data-testid="client-logout-btn" variant="ghost" size="sm" onClick={logout} className="text-[#6B6B6B] hover:text-[#C46A4A] hover:bg-[#C46A4A]/5">
              <LogOut className="w-4 h-4" />
            </Button></Tip>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <p className="section-num font-mono-tech text-xs tracking-widest text-tech mb-3 inline-flex items-center gap-2"><span className="tech-pulse" aria-hidden="true" /> ESPACE CLIENT // ONLINE</p>
        <h1 className="text-3xl sm:text-4xl font-medium mb-2 text-[#1F3535] font-montserrat tracking-tight">Bonjour, <span className="font-playfair italic text-[#C46A4A] font-normal">{user?.prenom || "Client"}</span></h1>
        <p className="text-[#6B6B6B] text-sm mb-10 font-body font-light">Bienvenue dans votre espace de gestion.</p>

        <Tabs defaultValue="overview" className="space-y-8">
          <TabsList className="bg-white border border-[#BFBFBF]/60 p-1 rounded-full shadow-sm">
            <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#1F3535] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-5 sm:px-6 text-xs sm:text-sm tracking-wide nav-text data-[state=active]:shadow-md data-[state=active]:shadow-[#1F3535]/20">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="options" data-testid="tab-options" className="data-[state=active]:bg-[#1F3535] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-5 sm:px-6 text-xs sm:text-sm tracking-wide nav-text data-[state=active]:shadow-md data-[state=active]:shadow-[#1F3535]/20">Options</TabsTrigger>
            <TabsTrigger value="requests" data-testid="tab-requests" className="data-[state=active]:bg-[#1F3535] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-5 sm:px-6 text-xs sm:text-sm tracking-wide nav-text data-[state=active]:shadow-md data-[state=active]:shadow-[#1F3535]/20">Demandes</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-[#1F3535] data-[state=active]:text-white text-[#6B6B6B] rounded-full px-5 sm:px-6 text-xs sm:text-sm tracking-wide nav-text data-[state=active]:shadow-md data-[state=active]:shadow-[#1F3535]/20">Mon profil</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {pendingPlanChange && (
              <div data-testid="pending-plan-change-banner" className="bg-gradient-to-br from-[#D4AF37]/[0.08] to-[#C46A4A]/[0.06] border border-[#D4AF37]/35 rounded-2xl p-5 flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#C46A4A] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#C46A4A] font-semibold nav-text mb-1.5">Demande en cours d'examen</p>
                  <p className="text-sm text-[#1F3535] font-body">
                    Votre demande de changement vers <strong className="font-medium">{plansCatalog?.plans?.[pendingPlanChange.requested_plan]?.name || pendingPlanChange.requested_plan}</strong> est en attente de validation. Nous reviendrons vers vous très rapidement.
                  </p>
                  <p className="text-xs text-[#6B6B6B] mt-1.5 font-body">Soumise le {new Date(pendingPlanChange.created_at).toLocaleDateString("fr-FR")}</p>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-5">
              <div className="relative bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/45 transition-colors">
                <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#C46A4A] font-semibold mb-2 nav-text">Forfait</p>
                <p className="text-lg font-medium text-[#1F3535] font-montserrat tracking-tight">{subscription?.plan?.name}</p>
                <p className="text-[#1F3535] mt-1.5"><span className="font-mono-tech font-semibold">{subscription?.plan?.price}</span> <span className="text-xs text-[#6B6B6B]">EUR Net/mois</span></p>
              </div>
              <div className="relative bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/45 transition-colors">
                <span aria-hidden="true" className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#C46A4A] font-semibold mb-2 nav-text">Options actives</p>
                <p className="text-lg font-medium text-[#1F3535] font-mono-tech">{subscription?.active_options?.length || 0}</p>
                <p className="text-[#6B6B6B] text-sm mt-1 font-body">{subscription?.active_options?.map(o => o.name).join(", ") || "Aucune"}</p>
              </div>
              <div className="relative bg-gradient-to-br from-[#1F3535] to-[#0F2424] text-white rounded-2xl p-6 ring-1 ring-[#D4AF37]/30 shadow-lg shadow-[#1F3535]/15 overflow-hidden">
                <div aria-hidden="true" className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none" />
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2 nav-text">Total mensuel</p>
                <p className="text-3xl font-mono-tech font-semibold text-white">{subscription?.total_monthly} <span className="text-base text-white/70">€</span></p>
                <p className="text-white/65 text-xs mt-1 font-body">Net/mois</p>
              </div>
            </div>
            <div className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-medium mb-4 flex items-center gap-2 text-[#1F3535] font-montserrat tracking-tight">
                <CreditCard className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.7} /> Détails de l'abonnement
              </h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div><p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.16em] nav-text mb-1">Début de l'abonnement</p><p className="font-medium text-[#1F3535] font-mono-tech">{subscription?.engagement_start ? new Date(subscription.engagement_start).toLocaleDateString("fr-FR") : "-"}</p></div>
                <div><p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.16em] nav-text mb-1">Engagement</p><p className="font-medium text-[#1F3535] font-mono-tech">{subscription?.engagement_end ? `6 mois · jusqu'au ${new Date(subscription.engagement_end).toLocaleDateString("fr-FR")}` : "6 mois"}</p></div>
              </div>
              <div className="mt-5 pt-5 border-t border-[#BFBFBF]/55 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-[#1F3535] font-body font-medium">Envie d'évoluer ?</p>
                  <p className="text-xs text-[#6B6B6B] font-body mt-0.5">Demandez un changement de forfait ou d'options. Nous étudions chaque demande individuellement.</p>
                </div>
                <Button
                  data-testid="open-plan-change-btn"
                  variant="outline"
                  size="sm"
                  disabled={!!pendingPlanChange}
                  onClick={openPlanChangeDialog}
                  className="border-[#1F3535]/30 text-[#1F3535] hover:bg-[#1F3535]/5 hover:border-[#D4AF37]/55 rounded-full text-[11px] tracking-[0.16em] uppercase font-semibold h-9 px-5 disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-2 text-[#D4AF37]" />
                  {pendingPlanChange ? "Demande en cours" : "Changer mon offre"}
                </Button>
              </div>
            </div>
            <div className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-medium mb-4 flex items-center gap-2 text-[#1F3535] font-montserrat tracking-tight">
                <FileText className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.7} /> Contrat
              </h3>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-sm text-[#555555] font-body">Votre contrat de prestation est disponible au téléchargement.</p>
                  <p className="text-xs text-[#1F3535] mt-1.5 flex items-center gap-1.5 font-body"><Check className="w-3 h-3 text-[#C46A4A]" strokeWidth={2.5} /> Signé électroniquement</p>
                </div>
                <Button data-testid="download-contract-btn" variant="outline" onClick={openContractPdf}
                  className="border-[#BFBFBF] text-[#1F3535] hover:bg-[#1F3535]/5 hover:border-[#D4AF37]/55 rounded-full transition-colors">
                  <Download className="w-4 h-4 mr-2" /> Télécharger PDF
                </Button>
              </div>
            </div>
            <div data-testid="payment-section" className="bg-white border border-[#1F3535]/15 rounded-2xl p-6 ring-1 ring-[#D4AF37]/15">
              <h3 className="font-medium mb-4 flex items-center gap-2 text-[#1F3535] font-montserrat tracking-tight">
                <CreditCard className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.7} /> Paiement mensuel
                {user?.subscription_active && (
                  <Badge data-testid="subscription-active-badge" className="bg-[#1F3535] text-[#D4AF37] border-0 text-[10px] font-semibold tracking-[0.16em] uppercase px-2.5 py-0.5 rounded-full ml-auto nav-text">
                    <Check className="w-3 h-3 mr-1 text-[#D4AF37]" strokeWidth={2.5} />Abonnement actif
                  </Badge>
                )}
              </h3>
              {user?.subscription_active ? (
                <>
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <p className="text-sm text-[#555555] font-body">Votre abonnement est actif et se renouvelle automatiquement chaque mois.</p>
                      <p className="text-2xl text-[#1F3535] mt-1.5"><span className="font-mono-tech font-semibold">{subscription?.total_monthly}</span> <span className="text-sm text-[#6B6B6B]">EUR Net/mois</span></p>
                    </div>
                  </div>
                  <button onClick={handleBillingPortal} disabled={billingLoading}
                    data-testid="stripe-billing-link"
                    className="w-full flex items-center justify-between p-4 rounded-xl bg-[#FBF9F5] hover:bg-[#1F3535]/5 border border-[#BFBFBF]/60 hover:border-[#D4AF37]/55 transition-all group cursor-pointer text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#1F3535]/8 flex items-center justify-center ring-1 ring-[#D4AF37]/20">
                        {billingLoading ? <Loader2 className="w-4 h-4 animate-spin text-[#1F3535]" /> : <CreditCard className="w-4 h-4 text-[#1F3535]" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[#1F3535] font-body">Gérer mon abonnement</p>
                        <p className="text-xs text-[#6B6B6B] font-body">Factures, moyens de paiement, historique, annulation</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#6B6B6B] group-hover:text-[#C46A4A] group-hover:translate-x-0.5 transition-all" />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-sm text-[#555555] font-body">Activez votre abonnement mensuel via paiement sécurisé Stripe.</p>
                      <p className="text-2xl text-[#1F3535] mt-1.5"><span className="font-mono-tech font-semibold">{subscription?.total_monthly}</span> <span className="text-sm text-[#6B6B6B]">EUR Net</span></p>
                    </div>
                    <Button data-testid="pay-subscription-btn" onClick={handlePaySubscription} disabled={paymentLoading}
                      className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full h-11 px-6 text-[11px] tracking-[0.16em] uppercase font-semibold shadow-md shadow-[#1F3535]/25 hover:shadow-lg hover:-translate-y-px transition-all">
                      {paymentLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Euro className="w-4 h-4 mr-2 text-[#D4AF37]" /> J'active mon abonnement</>}
                    </Button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#BFBFBF]/55">
                    <button onClick={handleBillingPortal} disabled={billingLoading}
                      data-testid="stripe-billing-link-inactive"
                      className="w-full flex items-center justify-between p-3.5 rounded-xl bg-[#FBF9F5] hover:bg-[#1F3535]/5 border border-[#BFBFBF]/60 hover:border-[#D4AF37]/55 transition-all group cursor-pointer text-left">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#1F3535]/8 flex items-center justify-center ring-1 ring-[#D4AF37]/20">
                          {billingLoading ? <Loader2 className="w-4 h-4 animate-spin text-[#1F3535]" /> : <CreditCard className="w-4 h-4 text-[#1F3535]" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1F3535] font-body">Gérer mon abonnement et mes factures</p>
                          <p className="text-xs text-[#6B6B6B] font-body">Factures, moyens de paiement, historique</p>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-[#6B6B6B] group-hover:text-[#C46A4A] group-hover:translate-x-0.5 transition-all" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          <TabsContent value="options" className="space-y-6">
            {!subscription?.options_allowed && (
              <div className="bg-gradient-to-br from-[#C46A4A]/[0.06] to-[#D4AF37]/[0.06] border border-[#C46A4A]/20 rounded-2xl p-5 text-center">
                <AlertTriangle className="w-5 h-5 text-[#C46A4A] mx-auto mb-2" strokeWidth={1.7} />
                <p className="text-sm text-[#1F3535] font-body">Les options mensuelles sont réservées aux clients <strong>Formule Croissance</strong>.</p>
                <p className="text-xs text-[#6B6B6B] mt-1 font-body italic">Contactez-nous pour upgrader votre forfait.</p>
              </div>
            )}
            {subscription?.active_options?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 text-[10px] text-[#C46A4A] uppercase tracking-[0.18em] nav-text">Options actives</h3>
                <div className="space-y-3">{subscription.active_options.map((opt) => (
                  <div key={opt.key} className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-5 flex items-center justify-between hover-lift hover:border-[#D4AF37]/45 transition-colors">
                    <div className="flex-1 cursor-pointer" onClick={() => setDetailDialog(opt)} data-testid={`active-option-detail-${opt.key}`}>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#1F3535] font-montserrat tracking-tight">{opt.name}</p>
                        <Badge className="bg-[#1F3535] text-[#D4AF37] border-0 text-[9px] tracking-[0.16em] uppercase font-semibold nav-text px-2">Active</Badge>
                      </div>
                      <p className="text-sm text-[#6B6B6B] mt-1 font-body">{opt.description}</p>
                      <p className="text-xs text-[#C46A4A] font-semibold mt-1.5 font-body inline-flex items-center gap-1">Voir le détail <ChevronRight className="w-3 h-3" /></p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-[#1F3535] whitespace-nowrap"><span className="font-mono-tech font-semibold">{opt.price}</span> <span className="text-xs text-[#6B6B6B]">EUR/mois</span></p>
                      <Button data-testid={`cancel-option-${opt.key}`} variant="ghost" size="sm" onClick={() => setCancelDialog(opt)} className="text-[#6B6B6B] hover:text-red-500 hover:bg-red-50"><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}</div>
              </div>
            )}
            {subscription?.options_allowed && subscription?.available_options?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3 text-[10px] text-[#C46A4A] uppercase tracking-[0.18em] nav-text">Options disponibles</h3>
                <div className="space-y-3">{subscription.available_options.map((opt) => (
                  <div key={opt.key} className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-5 hover:border-[#D4AF37]/45 transition-colors">
                    <div className="cursor-pointer" onClick={() => setDetailDialog(opt)} data-testid={`available-option-detail-${opt.key}`}>
                      <p className="font-medium text-[#1F3535] font-montserrat tracking-tight">{opt.name}</p>
                      <p className="text-sm text-[#6B6B6B] mt-1 font-body">{opt.description}</p>
                      {opt.includes && <div className="flex flex-wrap gap-2 mt-2">{opt.includes.map((inc, i) => (
                        <span key={i} className="text-xs bg-[#FBF9F5] border border-[#BFBFBF]/50 px-2.5 py-0.5 rounded-full text-[#1F3535] font-body">{inc}</span>
                      ))}</div>}
                      <p className="text-xs text-[#C46A4A] font-semibold mt-2 font-body inline-flex items-center gap-1">Voir le détail <ChevronRight className="w-3 h-3" /></p>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#BFBFBF]/55">
                      <p className="text-[#1F3535] whitespace-nowrap"><span className="font-mono-tech font-semibold">{opt.price}</span> <span className="text-xs text-[#6B6B6B]">EUR/mois</span></p>
                      <Button data-testid={`subscribe-option-${opt.key}`} size="sm" onClick={() => setSubscribeDialog(opt)}
                        className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full text-[11px] tracking-[0.16em] uppercase font-semibold px-5 h-9 shadow-md shadow-[#1F3535]/20"><Plus className="w-3.5 h-3.5 mr-1.5" /> Souscrire</Button>
                    </div>
                  </div>
                ))}</div>
              </div>
            )}

            {/* Studio Branding */}
            <div>
              <h3 className="font-semibold mb-1 text-[10px] text-[#C46A4A] uppercase tracking-[0.18em] nav-text">Studio Branding</h3>
              <p className="text-sm text-[#6B6B6B] mb-3 font-playfair italic">Votre ADN visuel, dessiné avec soin.</p>
              <div className="space-y-3">{subscription?.branding?.map((b) => (
                <div key={b.key} className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-5 flex items-center justify-between hover:border-[#D4AF37]/45 transition-colors flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#C46A4A]/10 border border-[#C46A4A]/20 flex items-center justify-center flex-shrink-0">
                      <Palette className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.7} />
                    </div>
                    <div>
                      <p className="font-medium text-[#1F3535] font-montserrat tracking-tight">{b.name}</p>
                      <p className="text-sm text-[#6B6B6B] mt-0.5 font-body">{b.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-[#1F3535] whitespace-nowrap"><span className="font-mono-tech font-semibold">{b.type === "on_quote" ? "Sur devis" : `${b.price} EUR`}</span></p>
                    {b.type === "on_quote" ? (
                      <Button data-testid={`branding-quote-${b.key}`} variant="outline" size="sm" onClick={() => { setModType("branding"); setModMessage(`Je souhaite un devis pour : ${b.name}`); setModDialog(true); }}
                        className="border-[#BFBFBF] text-[#1F3535] hover:bg-[#C46A4A]/5 hover:border-[#C46A4A]/40 rounded-full text-[11px] tracking-wide uppercase"><Send className="w-3.5 h-3.5 mr-1.5" /> Demander un devis</Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button data-testid={`branding-request-${b.key}`} variant="outline" size="sm" onClick={() => { setModType("branding"); setModMessage(`Je souhaite faire la demande pour : ${b.name} (${b.price} EUR)`); setModDialog(true); }}
                          className="border-[#BFBFBF] text-[#1F3535] hover:bg-[#C46A4A]/5 hover:border-[#C46A4A]/40 rounded-full text-[11px] tracking-wide uppercase"><Send className="w-3.5 h-3.5 mr-1.5" /> Faire la demande</Button>
                        <Button data-testid={`branding-order-${b.key}`} size="sm" onClick={() => handleOrderBranding(b.key)} disabled={brandingLoading === b.key}
                          className="bg-[#C46A4A] text-white hover:bg-[#A6553A] rounded-full text-[11px] tracking-[0.16em] uppercase font-semibold px-4 shadow-md shadow-[#C46A4A]/20">
                          {brandingLoading === b.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> Commander</>}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}</div>
            </div>
          </TabsContent>

          <TabsContent value="requests" className="space-y-6">
            <div className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors">
              <h3 className="font-medium mb-3 text-[#1F3535] font-montserrat tracking-tight text-lg">Faire une demande</h3>
              <p className="text-sm text-[#6B6B6B] mb-5 font-body">Demande de modification de votre site, mise à jour de vos informations de paiement, ou suspension de votre abonnement.</p>
              <Button data-testid="open-modification-dialog" onClick={() => setModDialog(true)} className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full text-[11px] tracking-[0.16em] uppercase font-semibold h-11 px-6 shadow-md shadow-[#1F3535]/20 hover:shadow-lg hover:-translate-y-px transition-all">
                <Send className="w-3.5 h-3.5 mr-2" /> Nouvelle demande
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <ClientProfileSection user={user} onUpdate={checkAuth} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Plan Change Request Dialog */}
      <Dialog open={planChangeOpen} onOpenChange={setPlanChangeOpen}>
        <DialogContent className="bg-white border-[#BFBFBF] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-montserrat tracking-tight">Demander un changement d'offre</DialogTitle>
            <DialogDescription className="text-[#6B6B6B] font-body">
              Choisissez votre nouveau forfait. Votre demande sera étudiée puis validée par votre interlocuteur. Un nouveau contrat sera généré après validation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Nouveau forfait souhaité</Label>
              <div className="space-y-2">
                {plansCatalog?.plans && Object.entries(plansCatalog.plans).map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`plan-change-option-${key}`}
                    onClick={() => { setPlanChangePlan(key); if (!p.options_allowed) setPlanChangeOptions([]); }}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${planChangePlan === key ? "border-[#1F3535] bg-[#1F3535]/[0.03] ring-1 ring-[#D4AF37]/40" : "border-[#BFBFBF]/60 hover:border-[#1F3535]/40 hover:bg-[#FBF9F5]"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-[#1F3535] font-montserrat">{p.name}</p>
                        <p className="text-xs text-[#6B6B6B] font-body mt-0.5 line-clamp-1">{p.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-[#1F3535] font-mono-tech font-semibold">{p.price}€</p>
                        <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider">Net/mois</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {planChangePlan && plansCatalog?.plans?.[planChangePlan]?.options_allowed && (
              <div>
                <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Options souhaitées (facultatif)</Label>
                <div className="space-y-2">
                  {plansCatalog?.options && Object.entries(plansCatalog.options).map(([key, o]) => (
                    <label
                      key={key}
                      data-testid={`plan-change-opt-${key}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${planChangeOptions.includes(key) ? "border-[#1F3535] bg-[#1F3535]/[0.03]" : "border-[#BFBFBF]/60 hover:border-[#1F3535]/40"}`}
                    >
                      <input
                        type="checkbox"
                        checked={planChangeOptions.includes(key)}
                        onChange={() => togglePlanChangeOption(key)}
                        className="w-4 h-4 accent-[#1F3535]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#1F3535] font-body font-medium">{o.name}</p>
                        <p className="text-xs text-[#6B6B6B] font-body line-clamp-1">{o.description}</p>
                      </div>
                      <span className="text-xs font-mono-tech text-[#1F3535] font-semibold flex-shrink-0">+{o.price}€</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Message (facultatif)</Label>
              <Textarea
                data-testid="plan-change-message"
                value={planChangeMessage}
                onChange={(e) => setPlanChangeMessage(e.target.value)}
                placeholder="Précisez la raison de votre demande, vos attentes…"
                className="bg-[#FBF9F5] border-[#BFBFBF] placeholder:text-[#6B6B6B]/40 min-h-[80px]"
              />
            </div>

            <div className="bg-[#FBF9F5] border border-[#BFBFBF]/60 rounded-lg p-3.5 text-xs text-[#6B6B6B] font-body leading-relaxed">
              <AlertTriangle className="w-3.5 h-3.5 text-[#C46A4A] inline mr-1.5" strokeWidth={2} />
              Conformément à l'<strong className="text-[#1F3535]">Article 3 bis</strong> de nos CGV, tout changement validé entraîne la signature d'un <strong className="text-[#1F3535]">nouveau contrat</strong>. La proratisation du paiement est gérée automatiquement par Stripe.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlanChangeOpen(false)} className="text-[#6B6B6B]">Annuler</Button>
            <Button
              data-testid="submit-plan-change-btn"
              onClick={handleSubmitPlanChange}
              disabled={actionLoading || !planChangePlan}
              className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full text-[11px] tracking-[0.16em] uppercase font-semibold"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-2" /> Envoyer la demande</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscribe Option Dialog */}
      <Dialog open={!!subscribeDialog} onOpenChange={() => setSubscribeDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader>
            <DialogTitle className="font-montserrat tracking-tight">Souscrire à une option</DialogTitle>
            <DialogDescription className="text-[#6B6B6B]">
              Confirmez-vous la souscription à l'option <strong className="text-[#2F4F4F]">{subscribeDialog?.name}</strong> pour <strong className="text-[#2F4F4F]">{subscribeDialog?.price} EUR/mois</strong> ?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg p-4 text-sm text-[#6B6B6B]">
            <AlertTriangle className="w-4 h-4 text-[#3F6B6B] inline mr-2" />
            L'option sera mise en place après discussion avec votre interlocuteur dédié.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSubscribeDialog(null)} className="text-[#6B6B6B]">Annuler</Button>
            <Button data-testid="confirm-subscribe-option" onClick={handleSubscribeOption} disabled={actionLoading}
              className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Option Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF]">
          <DialogHeader>
            <DialogTitle className="font-montserrat tracking-tight">Annuler une option</DialogTitle>
            <DialogDescription className="text-[#6B6B6B]">
              Confirmez-vous l'annulation de l'option <strong className="text-[#2F4F4F]">{cancelDialog?.name}</strong> ?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg p-4 text-sm text-[#6B6B6B]">
            <AlertTriangle className="w-4 h-4 text-[#3F6B6B] inline mr-2" />
            Votre demande sera prise en compte pour votre prochain prélèvement. L'option restera active jusqu'à sa date de renouvellement.
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelDialog(null)} className="text-[#6B6B6B]">Garder l'option</Button>
            <Button data-testid="confirm-cancel-option" onClick={handleCancelOption} disabled={actionLoading}
              className="bg-red-600 text-white hover:bg-red-700 rounded-full">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modification Dialog */}
      <Dialog open={modDialog} onOpenChange={(open) => { setModDialog(open); if (!open) setModFiles([]); }}>
        <DialogContent className="bg-white border-[#BFBFBF] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-montserrat tracking-tight">Nouvelle demande</DialogTitle>
            <DialogDescription className="text-[#6B6B6B]">Décrivez votre demande et nous vous recontacterons rapidement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Type de demande</label>
              <Select value={modType} onValueChange={setModType}>
                <SelectTrigger data-testid="mod-type-select" className="bg-[#FBF9F5] border-[#BFBFBF]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-[#BFBFBF]">
                  <SelectItem value="site_update">Mise à jour du site</SelectItem>
                  <SelectItem value="payment_update">Modification de paiement</SelectItem>
                  <SelectItem value="branding">Demande branding</SelectItem>
                  <SelectItem value="suspension">Demande de suspension</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Votre message</label>
              <Textarea data-testid="mod-message-textarea" value={modMessage} onChange={(e) => setModMessage(e.target.value)} placeholder="Décrivez votre demande..."
                className="bg-[#FBF9F5] border-[#BFBFBF] placeholder:text-[#6B6B6B]/40 min-h-[100px]" />
            </div>
            {/* File upload */}
            <div>
              <label className="text-xs text-[#6B6B6B] uppercase tracking-wider mb-2 block">Pièces jointes (max 5 Mo par fichier)</label>
              <div className="space-y-2">
                {modFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#FBF9F5] border border-[#BFBFBF] rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 text-[#3F6B6B] flex-shrink-0" />
                      <span className="text-xs text-[#2F4F4F] truncate">{f.name}</span>
                      <span className="text-xs text-[#6B6B6B]/60 flex-shrink-0">({(f.size / 1024).toFixed(0)} Ko)</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-[#6B6B6B] hover:text-red-500 transition-colors p-1" data-testid={`remove-file-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-[#BFBFBF] rounded-lg p-4 cursor-pointer hover:border-[#3F6B6B]/40 hover:bg-[#3F6B6B]/5 transition-colors" data-testid="file-upload-area">
                  <Paperclip className="w-4 h-4 text-[#3F6B6B]" />
                  <span className="text-xs text-[#6B6B6B]">Ajouter des fichiers (images, PDF, documents...)</span>
                  <input type="file" multiple className="hidden" onChange={handleFileSelect} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setModDialog(false); setModFiles([]); }} className="text-[#6B6B6B]">Annuler</Button>
            <Button data-testid="submit-modification-btn" onClick={handleModification} disabled={actionLoading || !modMessage.trim()}
              className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Detail Option Dialog */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="bg-white border-[#BFBFBF] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-montserrat tracking-tight">{detailDialog?.name}</DialogTitle>
            <DialogDescription className="text-[#3F6B6B] font-semibold">{detailDialog?.price} EUR/mois</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {detailDialog?.detail?.tagline && (
              <p className="text-sm text-[#3F6B6B] italic font-playfair">{detailDialog.detail.tagline}</p>
            )}
            {detailDialog?.detail?.intro && (
              <p className="text-sm text-[#555555] leading-relaxed">{detailDialog.detail.intro}</p>
            )}
            {detailDialog?.detail?.sections?.map((section, i) => (
              <div key={i}>
                <h4 className="text-xs uppercase tracking-widest text-[#3F6B6B] font-semibold mb-2">{section.title}</h4>
                {section.text && <p className="text-sm text-[#555555] leading-relaxed mb-2">{section.text}</p>}
                {section.items && <div className="space-y-2">{section.items.map((it, j) => (
                  <div key={j} className="flex items-start gap-2 text-sm text-[#555555]">
                    <Check className="w-3.5 h-3.5 text-[#3F6B6B] mt-0.5 flex-shrink-0" /><span>{it}</span>
                  </div>
                ))}</div>}
              </div>
            ))}
            {detailDialog?.detail?.items && !detailDialog?.detail?.sections && (
              <div className="space-y-3">{detailDialog.detail.items.map((it, j) => (
                <div key={j} className="flex items-start gap-2 text-sm text-[#555555]">
                  <Check className="w-3.5 h-3.5 text-[#3F6B6B] mt-0.5 flex-shrink-0" /><span>{it}</span>
                </div>
              ))}</div>
            )}
            {detailDialog?.detail?.engagement && (
              <div className="bg-[#3F6B6B]/5 border border-[#3F6B6B]/15 rounded-lg p-3">
                <p className="text-xs text-[#2F4F4F] font-semibold">Engagement : {detailDialog.detail.engagement}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailDialog(null)} className="text-[#6B6B6B]">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome popup after contract signature */}
      <Dialog open={welcomeOpen} onOpenChange={setWelcomeOpen}>
        <DialogContent
          data-testid="welcome-dialog"
          className="sm:max-w-md border-0 bg-white p-0 overflow-hidden animate-in fade-in-0 duration-700 ring-1 ring-[#D4AF37]/30"
        >
          <div className="relative bg-gradient-to-br from-[#1F3535] to-[#0F2424] px-8 pt-10 pb-9 text-center overflow-hidden">
            <div aria-hidden="true" className="absolute -top-20 -right-20 w-44 h-44 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.22),transparent_60%)] pointer-events-none" />
            <div aria-hidden="true" className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full bg-[radial-gradient(circle,rgba(196,106,74,0.18),transparent_60%)] pointer-events-none" />
            <div className="relative">
              <div className="w-14 h-14 rounded-full bg-white/10 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-5 backdrop-blur-sm">
                <Sparkles className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.7} />
              </div>
              <h2 className="text-[24px] font-medium text-white leading-snug font-montserrat tracking-tight">
                Ravi de vous compter <span className="font-playfair italic text-[#E8D8A9] font-normal">parmi nous.</span>
              </h2>
            </div>
          </div>
          <div className="px-8 pt-7 pb-8">
            <p className="text-[#555555] text-sm leading-relaxed mb-7 font-body">
              Les formalités sont terminées, et le chantier de votre nouvel écrin digital peut enfin commencer.
              Pour que nous puissions activer vos services sans attendre et lancer les premières étapes de votre projet,
              je vous invite à finaliser l'activation de votre abonnement mensuel dans votre espace.
            </p>
            <Button
              data-testid="welcome-activate-btn"
              onClick={handleWelcomeActivate}
              className="w-full bg-[#1F3535] text-white hover:bg-[#0F2424] h-12 rounded-full font-semibold text-[11px] tracking-[0.16em] uppercase shadow-md shadow-[#1F3535]/25 hover:shadow-lg hover:-translate-y-px transition-all"
            >
              <Sparkles className="w-3.5 h-3.5 mr-2 text-[#D4AF37]" /> Activer ma {subscription?.plan?.name?.replace(/^La\s+/i, "") || "Vitrine"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
    </TooltipProvider>
  );
}


function ClientProfileSection({ user, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    nom: user?.nom || "", prenom: user?.prenom || "",
    denomination_sociale: user?.denomination_sociale || "",
    siret: user?.siret || "", telephone: user?.telephone || "",
    adresse_rue: user?.adresse_rue || "",
    adresse_code_postal: user?.adresse_code_postal || "",
    adresse_ville: user?.adresse_ville || "",
  });
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "mt-1.5 bg-[#FBF9F5] border-[#BFBFBF]/70 text-sm rounded-xl focus-visible:ring-2 focus-visible:ring-[#D4AF37]/40 focus-visible:border-[#1F3535] font-body";

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/client/profile", form);
      toast.success("Profil mis a jour");
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise a jour");
    } finally { setSaving(false); }
  };

  const handleCancel = () => {
    setForm({
      nom: user?.nom || "", prenom: user?.prenom || "",
      denomination_sociale: user?.denomination_sociale || "",
      siret: user?.siret || "", telephone: user?.telephone || "",
      adresse_rue: user?.adresse_rue || "",
      adresse_code_postal: user?.adresse_code_postal || "",
      adresse_ville: user?.adresse_ville || "",
    });
    setEditing(false);
  };

  const fields = [
    { key: "prenom", label: "Prenom" },
    { key: "nom", label: "Nom" },
    { key: "denomination_sociale", label: "Denomination sociale" },
    { key: "siret", label: "SIRET" },
    { key: "telephone", label: "Telephone" },
    { key: "adresse_rue", label: "Adresse (rue)" },
    { key: "adresse_code_postal", label: "Code postal" },
    { key: "adresse_ville", label: "Ville" },
  ];

  return (
    <div className="space-y-6">
      {/* Site web button */}
      {user?.site_web && (
        <a href={user.site_web.startsWith("http") ? user.site_web : `https://${user.site_web}`} target="_blank" rel="noopener noreferrer"
          data-testid="visit-website-btn"
          className="relative flex items-center justify-between p-4 rounded-2xl bg-gradient-to-br from-[#1F3535] to-[#0F2424] hover:from-[#0F2424] hover:to-[#0F2424] transition-all group cursor-pointer ring-1 ring-[#D4AF37]/30 shadow-lg shadow-[#1F3535]/15 overflow-hidden">
          <div aria-hidden="true" className="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.18),transparent_60%)] pointer-events-none" />
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.7} />
            </div>
            <div>
              <p className="text-sm font-medium text-white font-body">Visiter mon site web</p>
              <p className="text-xs text-white/60 font-mono-tech">{user.site_web}</p>
            </div>
          </div>
          <ExternalLink className="relative w-4 h-4 text-white/60 group-hover:text-[#D4AF37] group-hover:translate-x-0.5 transition-all" />
        </a>
      )}

      {/* Profile card */}
      <div className="bg-white border border-[#BFBFBF]/55 rounded-2xl p-6 hover:border-[#D4AF37]/40 transition-colors">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="font-medium flex items-center gap-2 text-[#1F3535] font-montserrat tracking-tight text-lg">
            <User className="w-4 h-4 text-[#C46A4A]" strokeWidth={1.7} /> Mes informations
          </h3>
          {!editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="border-[#BFBFBF] text-[#1F3535] hover:bg-[#C46A4A]/5 hover:border-[#C46A4A]/40 rounded-full text-[11px] tracking-wide uppercase">
              <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Modifier
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancel} className="border-[#BFBFBF] text-[#6B6B6B] rounded-full text-[11px] tracking-wide uppercase">Annuler</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#1F3535] text-white hover:bg-[#0F2424] rounded-full text-[11px] tracking-wide uppercase font-semibold gap-1.5 shadow-md shadow-[#1F3535]/20">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={2.5} />} Enregistrer
              </Button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="grid sm:grid-cols-2 gap-4">
            {fields.map(f => (
              <div key={f.key}>
                <Label className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.16em] nav-text font-semibold">{f.label}</Label>
                <Input data-testid={`profile-${f.key}`} value={form[f.key]} onChange={e => update(f.key, e.target.value)} className={inputCls} />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-5">
            {fields.map(f => (
              <div key={f.key}>
                <p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.16em] nav-text font-semibold mb-1">{f.label}</p>
                <p className="text-sm font-medium text-[#1F3535] font-body">{user?.[f.key] || <span className="text-[#999] italic">Non renseigné</span>}</p>
              </div>
            ))}
            <div>
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.16em] nav-text font-semibold mb-1">Email de contact</p>
              <p className="text-sm font-medium text-[#1F3535] font-mono-tech">{user?.email}</p>
            </div>
          </div>
        )}

        <p className="text-[10px] text-[#999] mt-6 italic font-body flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-[#D4AF37]/60" />
          Les modifications sont automatiquement synchronisées avec votre espace de facturation Stripe.
        </p>
      </div>
    </div>
  );
}
